import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useSession } from "./providers/AuthProvider";
import { normalizeRole, expandRoles } from "./lib/roles";
import { useI18n } from "./lib/i18n";
import { UserMenu } from "./components/UserMenu";
import { HeaderUserMenu } from "./components/HeaderUserMenu";
import { NotificationBell } from "./components/NotificationBell";
import { SystemAlertBanner } from "./components/SystemAlertBanner";
import { DemoBadge } from "./components/DemoBadge";
import { Bug } from "lucide-react";
import { Toaster } from "./components/ui/toaster";
import { GlobalUndoToast } from "./components/GlobalUndoToast";
import { VikarRoutes } from "./routes/VikarRoutes";
import { CookieProvider } from "./components/cookies/CookieProvider";
import { Footer } from "./components/Footer";
import BottomNav from "./components/BottomNav";
import FTUETour from "./components/FTUETour";
import OfflineIndicator from "./components/OfflineIndicator";
import { OfflineBanner } from "./components/ui/OfflineBanner";
import { VikarTabBar } from "./components/layout/VikarTabBar";
import { ReleaseManager } from "./components/releases/ReleaseManager";
import { SessionExpiryModal } from "./components/SessionExpiryModal";
import ErrorBoundary from "./components/ErrorBoundary.tsx";
import { isCapacitor } from "./lib/capacitor";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { MaintenanceShield } from "./components/MaintenanceShield";

// ─── Lazy page imports ────────────────────────────────────────────────────────
const EntryChooser            = lazy(() => import("./pages/EntryChooser"));
const Login                   = lazy(() => import("./pages/auth/Login"));
const Signup                  = lazy(() => import("./pages/auth/Signup"));
const ResetPassword           = lazy(() => import("./pages/auth/ResetPassword"));
const ResetConfirm            = lazy(() => import("./pages/auth/ResetConfirm"));
const AuthCallback            = lazy(() => import("./pages/AuthCallback"));
const VerifyEmail             = lazy(() => import("./pages/auth/VerifyEmail"));
const VerifyOTP               = lazy(() => import("./pages/auth/VerifyOTP"));
const TwoFactor               = lazy(() => import("./pages/TwoFactor.jsx"));
const PendingApproval         = lazy(() => import("./pages/PendingApproval"));
const Dashboard               = lazy(() => import("./pages/Dashboard"));
const DashboardKommune        = lazy(() => import("./pages/DashboardKommune.jsx"));
const DashboardAdmin          = lazy(() => import("./pages/DashboardAdmin.jsx"));
const Onboarding              = lazy(() => import("./pages/onboarding/Onboarding"));
const OnboardingSuccess       = lazy(() => import("./pages/onboarding/OnboardingSuccess"));
const OnboardingPending       = lazy(() => import("./pages/onboarding/OnboardingPending"));
const InfoRequest             = lazy(() => import("./pages/onboarding/InfoRequest"));
const AdminOverview           = lazy(() => import("./pages/admin/AdminOverview"));
const AdminKommuner           = lazy(() => import("./pages/admin/AdminKommuner"));
const AdminDepartments        = lazy(() => import("./pages/admin/AdminDepartments"));
const Approvals               = lazy(() => import("./pages/admin/Approvals"));
const ITOperations            = lazy(() => import("./pages/admin/ITOperations"));
const TestLab                 = lazy(() => import("./pages/admin/TestLab"));
const TestAllPages            = lazy(() => import("./pages/admin/TestAllPages"));
const SearchTerms             = lazy(() => import("./pages/admin/SearchTerms"));
const Søge                    = lazy(() => import("./pages/admin/Søge"));
const AdminStaffingRequests   = lazy(() => import("./pages/admin/StaffingRequests"));
const CreateShift             = lazy(() => import("./pages/admin/CreateShift"));
const ManageShifts            = lazy(() => import("./pages/admin/ManageShifts"));
const VikarDirectory          = lazy(() => import("./pages/admin/VikarDirectory"));
const VikarDetail             = lazy(() => import("./pages/admin/VikarDetail"));
const Economy                 = lazy(() => import("./pages/admin/Economy"));
const Organization            = lazy(() => import("./pages/admin/Organization"));
const Reports                 = lazy(() => import("./pages/admin/Reports"));
const Lonkoersel              = lazy(() => import("./pages/admin/Lonkoersel"));
const CreateVirksomhed        = lazy(() => import("./pages/admin/CreateVirksomhed"));
const KommuneAnmodning        = lazy(() => import("./pages/KommuneAnmodning.jsx"));
const KommuneAnmodninger      = lazy(() => import("./pages/KommuneAnmodninger"));
const ShiftPlanning           = lazy(() => import("./pages/ShiftPlanning.jsx"));
const KommuneOpstart          = lazy(() => import("./pages/KommuneOpstart.jsx"));
const KommuneDokumenter       = lazy(() => import("./pages/KommuneDokumenter.jsx"));
const KommuneRapporter        = lazy(() => import("./pages/KommuneRapporter.jsx"));
const AuditTrail              = lazy(() => import("./pages/kommune/AuditTrail"));
const StaffingRequestTemplates = lazy(() => import("./pages/kommune/StaffingRequestTemplates"));
const TeamManagement          = lazy(() => import("./pages/kommune/TeamManagement"));
const GodkendTimer            = lazy(() => import("./pages/kommune/GodkendTimer"));
const TimerArkiv              = lazy(() => import("./pages/kommune/TimerArkiv"));
const Timesheets              = lazy(() => import("./pages/Timesheets"));
const MedarbejderNew          = lazy(() => import("./pages/MedarbejderNew"));
const VirksomhedDetail        = lazy(() => import("./pages/VirksomhedDetail"));
const MyProfile               = lazy(() => import("./pages/MyProfile.jsx"));
const Documents               = lazy(() => import("./pages/Documents"));
const HelpCenter              = lazy(() => import("./pages/HelpCenter"));
const NotFound                = lazy(() => import("./pages/NotFound"));
const Privacy                 = lazy(() => import("./pages/Privacy"));
const Terms                   = lazy(() => import("./pages/Terms"));
const Support                 = lazy(() => import("./pages/Support"));
const Cookies                 = lazy(() => import("./pages/Cookies"));
const AccessibilityStatement  = lazy(() => import("./pages/AccessibilityStatement"));
const BugReportModal          = lazy(() => import("./components/feedback/BugReportModal").then(m => ({ default: m.BugReportModal })));
const SocialCases             = lazy(() => import("./pages/SocialCases.jsx"));
const Handleplaner            = lazy(() => import("./pages/Handleplaner.jsx"));
const HandleplanDetail        = lazy(() => import("./pages/HandleplanDetail.jsx"));
const Journal                 = lazy(() => import("./pages/Journal.jsx"));
const Kalender                = lazy(() => import("./pages/Kalender"));
const CaseList                = lazy(() => import("./pages/cases/CaseList"));
const CaseNew                 = lazy(() => import("./pages/cases/CaseNew"));
const CaseDetail              = lazy(() => import("./pages/cases/CaseDetail"));
const AdminCases              = lazy(() => import("./pages/admin/AdminCases"));

// ─── Page-level Suspense fallback ─────────────────────────────────────────────
const PageFallback = () => (
  <div className="flex min-h-[50vh] items-center justify-center bg-background">
    <div className="space-y-4 text-center">
      <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-border border-t-primary" />
      <p className="text-sm text-muted-foreground">Indlæser…</p>
    </div>
  </div>
);

/** Wraps a lazy page in Suspense with the standard fallback */
const S = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageFallback />}>{children}</Suspense>
);

// Inline route guard — enforces authentication AND role-based access.
// Reads role from AuthProvider (single Supabase fetch, shared across all guards).
const Guard: React.FC<{ roles?: string[]; children: React.ReactNode }> = ({ roles = [], children }) => {
  const { user, role, loading } = useSession();
  const location = useLocation();

  // Still resolving auth + profile — show spinner, never redirect prematurely
  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-border border-t-primary" />
          <p className="text-sm text-muted-foreground">Indlæser…</p>
        </div>
      </div>
    );
  }

  // Not authenticated → send to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role check: if the route declares required roles, enforce them.
  // Both sides are normalized to canonical taxonomy (VIKAR | KOMMUNE | ADMIN),
  // so legacy strings like "CASE_WORKER" in caller arrays collapse to KOMMUNE
  // and still match a KOMMUNE-role user. A null role (profile still loading
  // or no profile) fails the check.
  if (roles.length > 0) {
    const canonicalRole = role ? normalizeRole(role) : null;
    const allowed = expandRoles(roles);
    if (!canonicalRole || !allowed.includes(canonicalRole)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

/**
 * Header - Main application header
 * Apple-inspired clean design with proper spacing
 * Mobile-first responsive design
 * Single source of truth for branding
 */
const Header = () => {
  const { t } = useI18n();
  const { user } = useSession();
  const brandLogoSrc = "/logo.svg"; // Use the real transparent SVG logo
  const inCapacitor = isCapacitor();
  const [bugReportOpen, setBugReportOpen] = React.useState(false);

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-app-border bg-app-surface overflow-visible"
      style={inCapacitor ? { paddingTop: 'env(safe-area-inset-top, 0px)' } : {}}
    >
      <div className="container mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo and Brand */}
        <Link to="/" className="flex items-center gap-0.5 transition-opacity hover:opacity-80 min-w-0 flex-shrink-0">
          <img
            src={brandLogoSrc}
            alt={t("common.brand")}
            className="h-8 w-auto flex-shrink-0"
            onError={(e) => {
              // Fallback if logo fails to load
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <span className="text-base sm:text-lg font-semibold text-app-text truncate">
            {t("common.brand")}
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {/* Dynamic navigation based on user role */}
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {user ? (
            <>
              <button
                onClick={() => setBugReportOpen(true)}
                className="relative flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:bg-muted transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Rapporter en fejl"
                title="Rapporter en fejl"
                type="button"
              >
                <Bug className="h-5 w-5" />
              </button>
              <NotificationBell />
            </>
          ) : null}
          <HeaderUserMenu />
        </div>
      </div>

      {/* Bug Report Modal */}
      {user && (
        <Suspense fallback={null}>
          <BugReportModal open={bugReportOpen} onOpenChange={setBugReportOpen} />
        </Suspense>
      )}
    </header>
  );
};

/**
 * AppShell - Main application shell
 * Apple-inspired clean layout with proper spacing rhythm
 * iOS-like mobile experience with safe area support
 * Single header, no duplicates
 */
const AppShell = ({ children }: { children: React.ReactNode }) => {
  const { t } = useI18n();
  const location = useLocation();
  const { user, role } = useSession();
  const inCapacitor = isCapacitor();

  // BottomNav / VikarTabBar expect lowercase role string
  const userRole = role ? role.toLowerCase() : null;

  // Initialize push notifications when in Capacitor wrapper
  usePushNotifications();

  // Skyddsnät mot fastnade scroll-lås: modaler sätter body.overflow='hidden' och
  // ska städa efter sig, men en krasch/avbruten unmount lämnar låset kvar för
  // resten av flik-sessionen ("kan inte skrolla"-klassen av buggar). Vid varje
  // sidbyte: finns inget öppet modal-lager → släpp låset.
  React.useEffect(() => {
    const t = setTimeout(() => {
      const lockHeld =
        document.body.style.overflow === 'hidden' ||
        document.body.classList.contains('modal-open');
      if (!lockHeld) return;
      const modalVisible = [...document.querySelectorAll<HTMLElement>('[role="dialog"], .fixed.inset-0')]
        .some((el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed');
      if (!modalVisible) {
        document.body.style.overflow = '';
        document.body.classList.remove('modal-open');
      }
    }, 800);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Network health check - DISABLED to avoid false positives
  const networkHealthDisabled = true;
  const networkLoading = false;
  const healthy = true;
  const networkError = null;
  const checkHealth = async () => {};

  // Hide header and footer on login pages and entry chooser for full immersion
  const isLoginPage =
    location.pathname === "/login" ||
    location.pathname.startsWith("/login/") ||
    location.pathname === "/admin/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/verify-email" ||
    location.pathname === "/verify-otp" ||
    location.pathname === "/two-factor" ||
    location.pathname === "/pending-approval" ||
    location.pathname === "/reset" ||
    location.pathname === "/reset/confirm";

  // Entry chooser should be fullscreen without header/footer (has its own)
  const isEntryChooser = location.pathname === "/";

  // Hide browser-specific elements when in Capacitor wrapper
  React.useEffect(() => {
    if (inCapacitor) {
      // Hide browser address bar and navigation elements
      document.documentElement.classList.add('capacitor-app');

      // Prevent pull-to-refresh on mobile browsers (handled by Capacitor)
      document.body.style.overscrollBehavior = 'none';

      // Set viewport meta tag for proper mobile display
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
      }
    }
  }, [inCapacitor]);

  return (
    <div className={`${isLoginPage || isEntryChooser ? 'fixed inset-0 w-screen h-screen overflow-hidden' : 'min-h-screen'} bg-app-bg flex flex-col`}>
      {/* System Alert Banner - shown to all authenticated users */}
      {!isLoginPage && !isEntryChooser && user && <SystemAlertBanner />}

      {/* Skip to main content link for accessibility */}
      {!isLoginPage && !isEntryChooser && (
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50 px-4 py-2 bg-app-primary text-app-primary-text rounded-lg font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2"
        >
          {t("common.skipToContent")}
        </a>
      )}

      {/* Single header component (hidden on login pages and entry chooser) */}
      {!isLoginPage && !isEntryChooser && <Header />}

      {/* Demo-markering — under headeren, synlig på alle skærme i demo-tilstand */}
      {!isLoginPage && !isEntryChooser && user && <DemoBadge />}

      {/* Main content with app-like spacing */}
      <main
        id="main-content"
        className={isLoginPage || isEntryChooser ? "w-full h-full overflow-hidden" : "flex-1 bg-app-bg overflow-x-hidden min-w-0"}
        style={{
          paddingBottom: isLoginPage || isEntryChooser ? '0' : 'max(env(safe-area-inset-bottom, 0px), 5rem)',
        }}
      >
        <div
          className={
            isLoginPage || isEntryChooser
              ? "w-full h-full flex flex-col overflow-hidden [&_button]:min-h-[44px]"
              : "w-full max-w-7xl mx-auto px-4 md:px-8 [&_button]:min-h-[44px]"
          }
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.18, ease: 'easeOut' } }}
              exit={{ opacity: 0, y: -4, transition: { duration: 0.12, ease: 'easeIn' } }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer (hidden on login pages, entry chooser, and in Capacitor wrapper) */}
      {!isLoginPage && !isEntryChooser && !inCapacitor && <Footer />}

      {/* Bottom Navigation (mobile only, hidden on login pages and entry chooser) */}
      {!isLoginPage && !isEntryChooser && userRole && userRole !== 'vikar' && (
        <BottomNav userRole={userRole} />
      )}

      {/* Vikar-specific tab bar (replaces BottomNav for vikar role) */}
      {!isLoginPage && !isEntryChooser && userRole === 'vikar' && (
        <VikarTabBar />
      )}

      {/* First-Time User Experience Tour */}
      {!isLoginPage && !isEntryChooser && user && <FTUETour />}

      {/* Offline indicator + offline banner */}
      <OfflineIndicator />
      <OfflineBanner />

    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <CookieProvider>
        <MaintenanceShield>
        <AppShell>
          <Routes>
            <Route path="/" element={<S><EntryChooser /></S>} />
            <Route path="/login" element={<S><Login /></S>} />
            {/* Redirect old login routes to unified login */}
            <Route path="/login/kommune" element={<Navigate to="/login" replace />} />
            <Route path="/login/dagbog" element={<Navigate to="/login" replace />} />
            <Route path="/admin/login" element={<Navigate to="/login" replace />} />
            <Route path="/signup" element={<S><Signup /></S>} />
            <Route path="/verify-email" element={<S><VerifyEmail /></S>} />
            <Route path="/verify-otp" element={<S><VerifyOTP /></S>} />
            <Route path="/reset" element={<S><ResetPassword /></S>} />
            <Route path="/reset/confirm" element={<S><ResetConfirm /></S>} />
            <Route path="/auth/callback" element={<S><AuthCallback /></S>} />
            <Route path="/onboarding" element={<S><Onboarding /></S>} />
            <Route path="/onboarding/info" element={<S><InfoRequest /></S>} />
            <Route path="/onboarding/success" element={<S><OnboardingSuccess /></S>} />
            <Route path="/onboarding/pending" element={<S><OnboardingPending /></S>} />
            <Route path="/two-factor" element={<S><TwoFactor /></S>} />
            <Route path="/pending-approval" element={<S><PendingApproval /></S>} />
            <Route path="/dashboard" element={
              <Guard roles={[]}>
                <S><Dashboard /></S>
              </Guard>
            } />
            <Route path="/dashboard/authenticated" element={
              <Guard roles={[]}>
                <S><Dashboard /></S>
              </Guard>
            } />
            <Route
              path="/dashboard/kommune"
              element={
                <Guard roles={["KOMMUNE", "ADMIN"]}>
                  <S><DashboardKommune /></S>
                </Guard>
              }
            />
            {VikarRoutes(Guard)}
            <Route
              path="/dashboard/admin"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><DashboardAdmin /></S>
                </Guard>
              }
            />
            <Route path="/admin" element={<Guard roles={["ADMIN"]}><S><AdminOverview /></S></Guard>} />
            <Route path="/admin/kommuner" element={<Guard roles={["ADMIN"]}><S><AdminKommuner /></S></Guard>} />
            <Route path="/admin/departments" element={<Guard roles={["ADMIN"]}><S><AdminDepartments /></S></Guard>} />
            <Route path="/admin/verification-queue" element={<Navigate to="/admin/approvals" replace />} />
            <Route path="/admin/roles" element={<Navigate to="/admin/approvals" replace />} />
            <Route path="/admin/tools" element={<Navigate to="/admin/approvals" replace />} />
            <Route
              path="/admin/approvals"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><Approvals /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/it-operations"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><ITOperations /></S>
                </Guard>
              }
            />
            {!import.meta.env.PROD && (
              <Route
                path="/admin/test-lab"
                element={
                  <Guard roles={["ADMIN"]}>
                    <S><TestLab /></S>
                  </Guard>
                }
              />
            )}
            <Route
              path="/admin/godkend-brugere"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><Approvals /></S>
                </Guard>
              }
            />
            {!import.meta.env.PROD && (
              <Route path="/admin/test-all-pages" element={<Guard roles={["ADMIN"]}><S><TestAllPages /></S></Guard>} />
            )}
            <Route path="/admin/sager" element={<Guard roles={["ADMIN"]}><S><AdminCases /></S></Guard>} />
            <Route
              path="/admin/search-terms"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><SearchTerms /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/søge"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><Søge /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/requests"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><AdminStaffingRequests /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/shifts"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><ManageShifts /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/shifts/create"
              element={
                <Guard roles={["ADMIN", "KOMMUNE"]}>
                  <S><CreateShift /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/vikare"
              element={
                <Guard roles={["ADMIN", "KOMMUNE"]}>
                  <S><VikarDirectory /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/vikare/:id"
              element={
                <Guard roles={["ADMIN", "KOMMUNE"]}>
                  <S><VikarDetail /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/økonomi"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><Economy /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/lonkoersel"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><Lonkoersel /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/organisation"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><Organization /></S>
                </Guard>
              }
            />
            <Route
              path="/admin/rapporter"
              element={
                <Guard roles={["ADMIN"]}>
                  <S><Reports /></S>
                </Guard>
              }
            />
            <Route path="/kommune/anmodning" element={<Guard roles={["KOMMUNE", "ADMIN"]}><S><KommuneAnmodning /></S></Guard>} />
            <Route path="/kommune/anmodninger" element={<Guard roles={["KOMMUNE", "ADMIN"]}><S><KommuneAnmodninger /></S></Guard>} />
            <Route
              path="/kommune/godkend-timer"
              element={
                <Guard roles={["KOMMUNE", "ADMIN"]}>
                  <S><GodkendTimer /></S>
                </Guard>
              }
            />
            <Route
              path="/kommune/timer-arkiv"
              element={
                <Guard roles={["KOMMUNE", "ADMIN"]}>
                  <S><TimerArkiv /></S>
                </Guard>
              }
            />
            <Route
              path="/timesheets"
              element={
                <Guard roles={["ADMIN", "KOMMUNE"]}>
                  <S><Timesheets /></S>
                </Guard>
              }
            />
            <Route path="/kommune/vagtplan" element={<Guard roles={["KOMMUNE", "ADMIN"]}><S><ShiftPlanning /></S></Guard>} />
            <Route path="/kommune/dokumenter" element={<Guard roles={["KOMMUNE", "ADMIN"]}><S><KommuneDokumenter /></S></Guard>} />
            <Route path="/kommune/opstart" element={<Guard roles={["KOMMUNE", "ADMIN"]}><S><KommuneOpstart /></S></Guard>} />
            <Route path="/kommune/rapporter" element={<Guard roles={["KOMMUNE", "ADMIN"]}><S><KommuneRapporter /></S></Guard>} />
            <Route path="/rapporter" element={<Guard roles={["KOMMUNE", "ADMIN"]}><S><KommuneRapporter /></S></Guard>} />
            <Route
              path="/kommune/audit-trail"
              element={
                <Guard roles={["KOMMUNE", "ADMIN"]}>
                  <S><AuditTrail /></S>
                </Guard>
              }
            />
            <Route
              path="/kommune/skabeloner"
              element={
                <Guard roles={["KOMMUNE", "ADMIN"]}>
                  <S><StaffingRequestTemplates /></S>
                </Guard>
              }
            />
            <Route
              path="/kommune/team"
              element={
                <Guard roles={["KOMMUNE", "ADMIN"]}>
                  <S><TeamManagement /></S>
                </Guard>
              }
            />
            <Route
              path="/dokumenter"
              element={
                <Guard roles={["KOMMUNE", "ADMIN"]}>
                  <S><Documents /></S>
                </Guard>
              }
            />
            <Route
              path="/hjaelp"
              element={
                <Guard roles={[]}>
                  <S><HelpCenter /></S>
                </Guard>
              }
            />
            <Route path="/medarbejder/ny" element={<Guard roles={["ADMIN"]}><S><MedarbejderNew /></S></Guard>} />
            <Route
              path="/virksomhed/ny"
              element={
                <Guard roles={["ADMIN", "KOMMUNE"]}>
                  <S><CreateVirksomhed /></S>
                </Guard>
              }
            />
            {/* Redirect aliases */}
            <Route path="/dashboard/kommune/vagter" element={<Navigate to="/kommune/vagtplan" replace />} />
            <Route path="/dashboard/kommune/profil" element={<Navigate to="/profile" replace />} />
            <Route path="/admin/vikarer" element={<Navigate to="/admin/vikare" replace />} />
            <Route path="/admin/economy" element={<Navigate to="/admin/økonomi" replace />} />
            <Route path="/admin/reports" element={<Navigate to="/admin/rapporter" replace />} />
            <Route path="/entry" element={<Navigate to="/" replace />} />
            <Route path="/virksomhed/:id" element={<Guard roles={["ADMIN", "KOMMUNE"]}><S><VirksomhedDetail /></S></Guard>} />
            <Route path="/support" element={<S><Support /></S>} />
            {/* Shared role routes: sager, handleplaner, journal */}
            <Route path="/social-cases" element={<Guard roles={["ADMIN","KOMMUNE","VIKAR"]}><S><SocialCases /></S></Guard>} />
            <Route path="/sager" element={<Guard roles={["ADMIN","KOMMUNE","VIKAR"]}><S><CaseList /></S></Guard>} />
            <Route path="/sager/ny" element={<Guard roles={["ADMIN","KOMMUNE"]}><S><CaseNew /></S></Guard>} />
            <Route path="/sager/:id" element={<Guard roles={["ADMIN","KOMMUNE","VIKAR"]}><S><CaseDetail /></S></Guard>} />
            <Route path="/handleplaner" element={<Guard roles={["ADMIN","KOMMUNE"]}><S><Handleplaner /></S></Guard>} />
            <Route path="/handleplaner/:id" element={<Guard roles={["ADMIN","KOMMUNE"]}><S><HandleplanDetail /></S></Guard>} />
            <Route path="/journal" element={<Guard roles={["ADMIN","KOMMUNE","VIKAR"]}><S><Journal /></S></Guard>} />
            <Route path="/kalender" element={<Guard roles={["VIKAR","KOMMUNE","ADMIN"]}><S><Kalender /></S></Guard>} />
            <Route path="/profile" element={<Guard roles={[]}><S><MyProfile /></S></Guard>} />
            <Route path="/my-profile" element={<Guard roles={[]}><S><MyProfile /></S></Guard>} />
            <Route path="/privacy" element={<S><Privacy /></S>} />
            <Route path="/privatlivspolitik" element={<S><Privacy /></S>} />
            <Route path="/cookies" element={<S><Cookies /></S>} />
            <Route path="/terms" element={<S><Terms /></S>} />
            <Route path="/tilgaengelighed" element={<S><AccessibilityStatement /></S>} />
            <Route path="*" element={<S><NotFound /></S>} />
          </Routes>
        </AppShell>
        <SessionExpiryModal />
        <Toaster />
        <GlobalUndoToast />
        <ReleaseManager />
        </MaintenanceShield>
      </CookieProvider>
    </BrowserRouter>
  );
}
